import { reversePayment, type PaymentOut } from '@myrentalmate/api-client';
import { Button, Chip, Link as MuiLink, Stack } from '@mui/material';
import { useState } from 'react';
import { Link } from 'react-router';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { required, type Field } from '../../components/FormFields';
import { formatDate, formatMoney } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { isOnlineMethod, KIND_LABELS, METHOD_LABELS } from './labels';

const REVERSE_FIELDS: Field[] = [
  {
    name: 'reason',
    label: 'Reason',
    required: true,
    helperText: 'For example, returned check',
  },
];

/** Payments with their receipts, on the lease page and the payments page. */
export function PaymentTable({
  payments,
  showLease = false,
}: {
  payments: PaymentOut[];
  showLease?: boolean;
}) {
  const canReverse = useCan('payments.manage');
  const [reversing, setReversing] = useState<PaymentOut | null>(null);
  const columns: DataColumn<PaymentOut>[] = [
    { label: 'Received', render: (p) => formatDate(p.received_on) },
    ...(showLease
      ? [
          {
            label: 'Rental',
            render: (p: PaymentOut) => (
              <MuiLink component={Link} to={`/staff/leases/${p.lease_id}`}>
                {p.lease_label}
              </MuiLink>
            ),
          },
          { label: 'From', render: (p: PaymentOut) => p.resident_names || '–' },
        ]
      : []),
    { label: 'Amount', render: (p) => formatMoney(p.amount) },
    { label: 'For', render: (p) => KIND_LABELS[p.kind] },
    {
      label: 'Paid by',
      // An online payment's reference is Stripe's id, for finding it there, not for reading here.
      render: (p) =>
        `${METHOD_LABELS[p.method]}${p.reference && !isOnlineMethod(p.method) ? ` ${p.reference}` : ''}`,
    },
    {
      label: 'Status',
      render: (p) =>
        p.reversed_at ? (
          <Chip size="small" color="error" label="Reversed" title={p.reversal_reason ?? ''} />
        ) : (
          <Chip size="small" color="success" label="Received" />
        ),
    },
    {
      label: 'Actions',
      align: 'right',
      render: (p) => (
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          <Button size="small" href={`/api/payments/${p.id}/receipt`}>
            Receipt
          </Button>
          {canReverse && !p.reversed_at && !isOnlineMethod(p.method) && (
            <Button size="small" onClick={() => setReversing(p)}>
              Reverse
            </Button>
          )}
        </Stack>
      ),
    },
  ];
  return (
    <>
      <DataTable columns={columns} rows={payments} rowKey={(p) => p.id} />
      {reversing && (
        <FormDialog
          title={`Reverse ${formatMoney(reversing.amount)} Payment`}
          intro="This puts the amount back on what the lease owes. The payment stays on record, and its receipt is marked void."
          fields={REVERSE_FIELDS}
          initial={{ reason: '' }}
          submitLabel="Reverse payment"
          onSubmit={(values) =>
            reversePayment(reversing.id, { reason: required(values, 'reason') })
          }
          onClose={() => setReversing(null)}
        />
      )}
    </>
  );
}
